import { useEffect, useState } from 'react';
import { agentReleases, devices as devicesApi,
  type AgentRelease, type AgentDeployment, type Device } from '../../api';

export default function AgentReleasesPage() {
  const [releases, setReleases] = useState<AgentRelease[]>([]);
  const [deployments, setDeployments] = useState<AgentDeployment[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showDeploy, setShowDeploy] = useState<AgentRelease | null>(null);

  async function refresh() {
    try {
      const [r, d, dv] = await Promise.all([
        agentReleases.list(),
        agentReleases.deployments(),
        devicesApi.list(),
      ]);
      setReleases(r.releases);
      setDeployments(d.deployments);
      setDevices(dv.devices);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, []);

  async function removeRelease(r: AgentRelease) {
    if (r.assigned_device_count > 0) {
      alert(`Cannot delete — ${r.assigned_device_count} device(s) still assigned. Unassign first.`);
      return;
    }
    if (!confirm(`Delete release ${r.version}?`)) return;
    setBusy(true);
    try {
      await agentReleases.remove(r.id);
      await refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  async function unassignDevice(deviceId: string) {
    if (!confirm('Stop trying to update this device?')) return;
    setBusy(true);
    try {
      await agentReleases.unassign([deviceId]);
      await refresh();
    } finally { setBusy(false); }
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1em' }}>
        <h2 style={{ margin: 0 }}>Agent Releases</h2>
        <button className="primary" onClick={() => setShowUpload(true)}>+ Upload release</button>
      </div>

      {err && <div className="error" style={{ marginBottom: '1em' }}>{err}</div>}

      <div className="card" style={{ background: 'rgba(59,130,246,0.08)', borderColor: '#3b82f6',
        fontSize: '0.85em', color: 'var(--fg-3)', marginBottom: '1em' }}>
        Build a new release tarball with <code>./tools/package-agent.sh &lt;version&gt;</code> on
        your dev machine, then upload it here. Pi devices poll for assigned releases every 60s
        and self-update with SHA-256 verification.
      </div>

      <h3>Releases</h3>
      {releases.length === 0 ? (
        <div style={{ color: 'var(--fg-3)', fontStyle: 'italic' }}>No releases uploaded yet.</div>
      ) : (
        <table style={{ width: '100%', marginBottom: '2em' }}>
          <thead>
            <tr>
              <th>Version</th><th>Released</th><th>By</th><th>Size</th>
              <th>Assigned</th><th>Applied</th><th>Notes</th><th></th>
            </tr>
          </thead>
          <tbody>
            {releases.map((r) => (
              <tr key={r.id}>
                <td><strong>{r.version}</strong>
                  <span style={{ color: 'var(--fg-3)', fontSize: '0.8em', marginLeft: '0.5em' }}>
                    {r.channel}
                  </span>
                </td>
                <td style={{ fontSize: '0.85em', color: 'var(--fg-3)' }}>
                  {new Date(r.released_at).toLocaleString()}
                </td>
                <td style={{ fontSize: '0.85em' }}>{r.released_by_email ?? '—'}</td>
                <td style={{ fontSize: '0.85em' }}>{formatBytes(r.size_bytes)}</td>
                <td>{r.assigned_device_count}</td>
                <td>{r.applied_device_count} / {r.assigned_device_count}</td>
                <td style={{ fontSize: '0.85em', maxWidth: 280, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.notes ?? ''}>
                  {r.notes ?? '—'}
                </td>
                <td>
                  <button className="primary" onClick={() => setShowDeploy(r)}>Deploy</button>{' '}
                  <button className="danger" disabled={busy || r.assigned_device_count > 0}
                    onClick={() => removeRelease(r)}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3>Device deployment status</h3>
      <table style={{ width: '100%' }}>
        <thead>
          <tr>
            <th>Device</th><th>Status</th><th>Last seen</th>
            <th>Running</th><th>Assigned</th><th>State</th><th></th>
          </tr>
        </thead>
        <tbody>
          {deployments.map((d) => {
            const reported = d.reported_version;
            const assigned = d.assigned_version;
            let stateLabel: { text: string; color: string };
            if (!assigned) stateLabel = { text: 'no assignment', color: 'var(--fg-3)' };
            else if (reported === assigned) stateLabel = { text: '✓ in sync', color: '#10b981' };
            else if (d.applied_at) stateLabel = { text: 'applied, awaiting heartbeat', color: '#f59e0b' };
            else stateLabel = { text: 'update pending', color: '#3b82f6' };
            return (
              <tr key={d.device_id}>
                <td>{d.device_name}</td>
                <td style={{ fontSize: '0.85em' }}>{d.status}</td>
                <td style={{ fontSize: '0.85em', color: 'var(--fg-3)' }}>
                  {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : 'never'}
                </td>
                <td style={{ fontFamily: 'monospace' }}>{reported ?? '—'}</td>
                <td style={{ fontFamily: 'monospace' }}>{assigned ?? '—'}</td>
                <td style={{ color: stateLabel.color, fontSize: '0.85em' }}>{stateLabel.text}</td>
                <td>
                  {assigned && (
                    <button onClick={() => unassignDevice(d.device_id)}
                      style={{ fontSize: '0.78em' }}>Clear</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {showUpload && (
        <UploadModal onClose={() => setShowUpload(false)} onUploaded={async () => {
          setShowUpload(false); await refresh();
        }} />
      )}
      {showDeploy && (
        <DeployModal release={showDeploy} devices={devices} deployments={deployments}
          onClose={() => setShowDeploy(null)}
          onDeployed={async () => { setShowDeploy(null); await refresh(); }} />
      )}
    </div>
  );
}

/* ============================================================ Upload modal */

function UploadModal({ onClose, onUploaded }: { onClose: () => void; onUploaded: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!file || !version) return;
    setBusy(true); setErr(null);
    try {
      await agentReleases.upload(file, version.trim(), notes.trim() || undefined);
      onUploaded();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  // Try to extract version from filename: iotech-agent-1.2.3.tar.gz → 1.2.3
  function onFile(f: File | null) {
    setFile(f);
    if (f && !version) {
      const m = f.name.match(/(\d+\.\d+\.\d+(?:[-+][A-Za-z0-9.-]+)?)/);
      if (m) setVersion(m[1]);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 520 }}>
        <h3 style={{ marginTop: 0 }}>Upload agent release</h3>
        <div className="field">
          <label>Tarball (.tar.gz from <code>./tools/package-agent.sh</code>)</label>
          <input type="file" accept=".tar.gz,.tgz,application/gzip"
            onChange={(e) => onFile(e.target.files?.[0] ?? null)} />
          {file && (
            <div style={{ fontSize: '0.82em', color: 'var(--fg-3)', marginTop: '0.3em' }}>
              {file.name} · {formatBytes(file.size)}
            </div>
          )}
        </div>
        <div className="field">
          <label>Version (semver)</label>
          <input type="text" value={version} placeholder="1.2.3"
            onChange={(e) => setVersion(e.target.value)} />
        </div>
        <div className="field">
          <label>Notes (optional)</label>
          <textarea value={notes} rows={3} placeholder="What changed in this release?"
            onChange={(e) => setNotes(e.target.value)} />
        </div>
        {err && <div className="error">{err}</div>}
        <div style={{ display: 'flex', gap: '0.5em', justifyContent: 'flex-end', marginTop: '1em' }}>
          <button onClick={onClose} disabled={busy}>Cancel</button>
          <button className="primary" disabled={!file || !version || busy} onClick={submit}>
            {busy ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================ Deploy modal */

function DeployModal({ release, devices, deployments, onClose, onDeployed }: {
  release: AgentRelease;
  devices: Device[];
  deployments: AgentDeployment[];
  onClose: () => void;
  onDeployed: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const dByDevice = new Map(deployments.map((d) => [d.device_id, d]));

  function toggle(id: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }
  function selectAll() { setSelected(new Set(devices.map((d) => d.id))); }
  function selectNone() { setSelected(new Set()); }

  async function deploy() {
    if (selected.size === 0) return;
    setBusy(true); setErr(null);
    try {
      await agentReleases.deploy(release.id, [...selected]);
      onDeployed();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <h3 style={{ marginTop: 0 }}>Deploy <code>{release.version}</code> to devices</h3>
        <div className="card" style={{ background: 'rgba(59,130,246,0.08)', borderColor: '#3b82f6',
          fontSize: '0.85em', color: 'var(--fg-3)' }}>
          Selected devices will download and install this release on their next poll (within 60s of online).
          Already-assigned devices will be re-targeted.
        </div>
        <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center', margin: '0.8em 0' }}>
          <button onClick={selectAll}>Select all</button>
          <button onClick={selectNone}>Select none</button>
          <span style={{ color: 'var(--fg-3)', fontSize: '0.85em' }}>
            {selected.size} of {devices.length} selected
          </span>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, border: '1px solid var(--border)', borderRadius: 6 }}>
          <table style={{ width: '100%' }}>
            <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-2)' }}>
              <tr>
                <th style={{ width: 30 }}></th>
                <th>Device</th><th>Running</th><th>Currently assigned</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => {
                const dep = dByDevice.get(d.id);
                return (
                  <tr key={d.id} onClick={() => toggle(d.id)} style={{ cursor: 'pointer' }}>
                    <td><input type="checkbox" checked={selected.has(d.id)}
                      onChange={() => toggle(d.id)} onClick={(e) => e.stopPropagation()} /></td>
                    <td>{d.name}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                      {dep?.reported_version ?? '—'}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                      {dep?.assigned_version ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {err && <div className="error" style={{ marginTop: '0.8em' }}>{err}</div>}
        <div style={{ display: 'flex', gap: '0.5em', justifyContent: 'flex-end', marginTop: '1em' }}>
          <button onClick={onClose} disabled={busy}>Cancel</button>
          <button className="primary" disabled={selected.size === 0 || busy} onClick={deploy}>
            {busy ? 'Deploying…' : `Deploy to ${selected.size} device${selected.size === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
